import type {
  TableCellKind,
  TableData,
  TableHeaderScope,
  TableSection,
} from './Table.types';

const KINDS: TableCellKind[] = ['text', 'markdown', 'empty'];
const SECTIONS: TableSection[] = ['head', 'body', 'foot'];
const SCOPES: TableHeaderScope[] = ['col', 'row'];

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

/**
 * Reads the Table Config field. The field is plain JSON, so anything can be in
 * it. Returns the table when it is one this site can draw, and null when it is
 * not, so a broken entry shows nothing instead of breaking the page.
 */
export const parseTableConfig = (value: unknown): TableData | null => {
  if (
    !isRecord(value) ||
    value.schemaVersion !== 1 ||
    !Array.isArray(value.rows) ||
    value.rows.length === 0
  ) {
    return null;
  }

  let columns = -1;

  for (const row of value.rows) {
    if (
      !isRecord(row) ||
      !SECTIONS.includes(row.section as TableSection) ||
      !Array.isArray(row.cells) ||
      row.cells.length === 0
    ) {
      return null;
    }

    let spanned = 0;
    for (const cell of row.cells) {
      if (
        !isRecord(cell) ||
        !KINDS.includes(cell.kind as TableCellKind) ||
        typeof cell.value !== 'string' ||
        !Number.isInteger(cell.colSpan) ||
        (cell.colSpan as number) < 1 ||
        (cell.header !== undefined &&
          !SCOPES.includes(cell.header as TableHeaderScope))
      ) {
        return null;
      }
      spanned += cell.colSpan as number;
    }

    if (columns === -1) {
      columns = spanned;
    } else if (spanned !== columns) {
      return null;
    }
  }

  return value as TableData;
};

// ---------------------------------------------------------------- Markdown
//
// Basic formatting for cells with kind "markdown": **bold**, _italic_,
// [links](url) and "- " bullet lists. Each non-empty line is its own
// paragraph, and consecutive "- " lines are one list. A backslash escapes
// \\ \* \_ \[ \] and a "-" at the start of a line.
//
// Bold, italic and links nest: **[a link](url)** is a bold link, and
// [**bold** words](url) is a link with bold in it. A link never holds another
// link.
//
// This is the same reading the Table Editor shows in its preview, so what an
// editor checks there is what the page draws.
//
// The Table Editor keeps its own copy of this reading and of isSafeHref, in
// plaid/contentful-apps at table-editor/src/lib/table/markdown.ts. The two have
// to stay the same, so a fix here needs the same fix there, with the same
// tests. Both test files cover the same hidden-character links for that reason.

export type Inline =
  | { type: 'text'; text: string }
  | { type: 'strong'; children: Inline[] }
  | { type: 'em'; children: Inline[] }
  | { type: 'link'; href: string; children: Inline[] };

export type Block =
  | { type: 'paragraph'; inline: Inline[] }
  | { type: 'list'; items: Inline[][] };

const ESCAPABLE = '\\*_[]-';

const isWordChar = (char: string | undefined): boolean => {
  return char !== undefined && /[\p{L}\p{N}]/u.test(char);
};

const SAFE_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

/**
 * Links may only point somewhere safe. Script and data URLs are refused.
 *
 * The URL parser decides, because it applies the same clean-up a browser does
 * before it reads a link: it drops tabs and newlines anywhere in the URL and
 * control characters at the start. Checking the raw text with a pattern would
 * miss `java<tab>script:` and similar. Relative links resolve against an https
 * base, so they pass.
 */
export const isSafeHref = (href: string): boolean => {
  try {
    return SAFE_PROTOCOLS.includes(new URL(href, 'https://plaid.com').protocol);
  } catch {
    return false;
  }
};

const parseInline = (source: string, allowLinks = true): Inline[] => {
  const out: Inline[] = [];
  let buffer = '';

  const flush = () => {
    if (buffer) {
      out.push({ type: 'text', text: buffer });
      buffer = '';
    }
  };

  let i = 0;
  while (i < source.length) {
    const char = source[i];

    if (
      char === '\\' &&
      i + 1 < source.length &&
      ESCAPABLE.includes(source[i + 1])
    ) {
      buffer += source[i + 1];
      i += 2;
      continue;
    }

    if (source.startsWith('**', i)) {
      const end = source.indexOf('**', i + 2);
      if (end > i + 2) {
        flush();
        out.push({
          type: 'strong',
          children: parseInline(source.slice(i + 2, end), allowLinks),
        });
        i = end + 2;
        continue;
      }
    }

    if (char === '_' && !isWordChar(source[i - 1])) {
      let end = -1;
      for (let j = i + 1; j < source.length; j += 1) {
        if (
          source[j] === '_' &&
          source[j - 1] !== '\\' &&
          !isWordChar(source[j + 1])
        ) {
          end = j;
          break;
        }
      }
      if (end > i + 1) {
        flush();
        out.push({
          type: 'em',
          children: parseInline(source.slice(i + 1, end), allowLinks),
        });
        i = end + 1;
        continue;
      }
    }

    if (allowLinks && char === '[') {
      const close = source.indexOf(']', i + 1);
      if (close > i + 1 && source[close + 1] === '(') {
        const end = source.indexOf(')', close + 2);
        if (end > close + 2) {
          // What is inside can be bold or italic, but not another link.
          const children = parseInline(source.slice(i + 1, close), false);
          const href = source.slice(close + 2, end).trim();
          flush();
          // An unsafe link keeps its words and loses the link.
          if (isSafeHref(href)) {
            out.push({ type: 'link', href, children });
          } else {
            out.push(...children);
          }
          i = end + 1;
          continue;
        }
      }
    }

    buffer += char;
    i += 1;
  }

  flush();
  return out;
};

export const parseMarkdown = (value: string): Block[] => {
  const blocks: Block[] = [];
  let list: Inline[][] | null = null;

  value.split(/\r?\n/).forEach((line) => {
    const item = /^- +(.*)$/.exec(line);
    if (item) {
      if (!list) {
        list = [];
        blocks.push({ type: 'list', items: list });
      }
      list.push(parseInline(item[1]));
      return;
    }

    list = null;
    if (line.trim()) {
      blocks.push({ type: 'paragraph', inline: parseInline(line.trim()) });
    }
  });

  return blocks;
};
